import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { AddFlaggedServices } from 'app/um/components/service-flags/add-flagged-services';
import { IPopup } from 'app/shared/elements/popup-base';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ServiceFlagReloadEvent } from 'app/um/lib/events';
import * as UM from 'app/um/models/essette-api';


@autoinject()
export class FlaggedCodes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    // For selectable Flag Type Dropdown
    protected serviceFlags: UM.AuthFlag[] = [];
    @observable selectedFlag: UM.AuthFlag | null = null;

    // For Flagged Codes Grid
    protected displayServices: UM.AuthService[] = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $addFlaggedServicesModal!: IPopup;
    protected $addFlaggedServices!: AddFlaggedServices;
    protected $flaggedServiceForm!: Element;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'code',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.loadServiceFlags();
    }

    protected attached(): void {
        // Subscribe event for service flag changes
        this.subscriptions.push(
            this.events.subscribe(ServiceFlagReloadEvent, (e: ServiceFlagReloadEvent) => {
                this.selectedFlag = null;
                this.loadServiceFlags();
            }));
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        if(this.resultCount > 1) {
            const { property, direction } = event.detail;
            const { sorting } = this;
            if(direction === null) {
                sorting.remove(property);
            } else {
                sorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    protected async selectedFlagChanged(newValue: UM.AuthFlag, oldValue: UM.AuthFlag): Promise<void> {
        if(newValue?.id) {
            if(this.$pager) {
                this.$pager.changePage(0);
            }
            await this.loadServices(newValue.id);
        }
        else {
            this.displayServices = [];
        }
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        if(this.selectedFlag?.id) {
            await this.loadServices(this.selectedFlag?.id);
        }
    }


    //#region Add Flagged Services Modal
    protected addFlaggedServices(): void {
        this.$addFlaggedServicesModal.open();
    }

    protected closeAddFlaggedServices(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$addFlaggedServicesModal.close();
    }

    protected async handleFormSuccess(): Promise<void> {
        this.closeAddFlaggedServices();
        await Promise.all([this.$addFlaggedServices.reload(), this.reload()]);
    }
    //#endregion Add Flagged Services Modal

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadServiceFlags(): Promise<void> {
        const includes: string[] = [
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            all: true
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthFlags(options, includes, 'services');
            this.serviceFlags = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadServices(flagId: string): Promise<void> {
        const includes: string[] = [
        ];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadServicesByFlag(flagId, options, includes);
            this.displayServices = response.results;
            this.resultCount = response.count ?? 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deleteFlagFromService(service: UM.AuthService): Promise<void> {
        if(!this.selectedFlag?.id) return;

        // Deactivation is all flagged-codes allows, so - always pop confirmation
        const result = await confirmation.showDelete('um:service.code.flagged.$', 1);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        let isSuccessful = false;

        const saveFlag: UM.AuthFlag = {
            id: this.selectedFlag.id,
            services: [{ id: service.id }]
        }

        try {
            this.state.beginSave();
            const response = await this.service.deleteAuthFlagDetail(saveFlag);
            isSuccessful = response.isSuccessful;
            await Promise.all([this.$addFlaggedServices.reload(), this.reload()]);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:service.code.flagged.$', isSuccessful);
            this.state.endSave();
        }
    }
}
